import type { Invoice } from "@freightsimple/generated-dashboard-openapi-client/models/Invoice";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import { InvoiceType } from "@freightsimple/generated-dashboard-openapi-client/models/InvoiceType";
import { PaymentMethod } from "@freightsimple/generated-dashboard-openapi-client/models/PaymentMethod";
import { useEffect, useReducer, useState } from "react";
import { isCreditCardExpired } from "@/Helpers/creditCardExpiry";
import { usePaymentMethodsApi } from "@/Hooks/usePaymentMethodsApi";
import {
  defaultPaymentMethodsState,
  paymentMethodsReducer,
} from "../Helpers/paymentsMakePaymentsMethodsState";
import { paymentsPayableInvoices } from "../Exports/Helpers/paymentsPayableInvoices";
import {
  isBankAccount,
  isCreditCard,
} from "../Helpers/paymentsMakePaymentsUtils";
import { paymentsResolvePaymentMethodText } from "../Helpers/paymentsResolvePaymentMethodText";
import type {
  InvoiceSummary,
  PaymentMethodsState,
} from "../Types/paymentsPaymentTypes";
import { PaymentsStatusType } from "../Types/PaymentsStatusType";

interface UsePaymentsMakePaymentsFlowArgs {
  /** Loading the available methods waits until the dialog actually opens. */
  open: boolean;
  invoices: Invoice[];
  shipments: Shipment[];
  refresh: () => Promise<void>;
}

interface PaymentsMakePaymentsFlow {
  loading: boolean;
  /** The payment methods failed to load, so there is nothing to pick from. */
  paymentMethodsLoadFailed: boolean;
  paymentMethodsState: PaymentMethodsState;
  reloadPaymentMethods: () => Promise<void>;
  selectedPaymentMethod: string | undefined;
  setSelectedPaymentMethod: (paymentMethodId: string) => void;
  paymentReference: string;
  setPaymentReference: (paymentReference: string) => void;
  status: PaymentsStatusType;
  errorMessage: string;
  paidInvoices: Invoice[];
  paidTotalAmount: number;
  invoicesToPay: Invoice[];
  shipmentMap: Map<string, Shipment>;
  includedInPayment: Invoice[];
  initialSelectedInvoiceIds: Set<string>;
  setSelectedInvoiceIds: (selectedInvoiceIds: Set<string>) => void;
  totalAmountToPay: number;
  invoiceSummary: InvoiceSummary;
  confirmPayment: () => Promise<void>;
}

/**
 * The Make Payments state machine: which invoices are in the payment, which
 * method pays them, and where the payment stands (idle → processing →
 * completed/pending/failed). The dialog component just renders it.
 */
export function usePaymentsMakePaymentsFlow({
  open,
  invoices,
  shipments,
  refresh,
}: UsePaymentsMakePaymentsFlowArgs): PaymentsMakePaymentsFlow {
  const paymentMethodsApi = usePaymentMethodsApi();

  const [loading, setLoading] = useState(true);
  const [paymentMethodsLoadFailed, setPaymentMethodsLoadFailed] =
    useState(false);
  const [paymentMethodsState, dispatch] = useReducer(
    paymentMethodsReducer,
    defaultPaymentMethodsState,
  );
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    string | undefined
  >(undefined);
  const [paymentReference, setPaymentReference] = useState("");
  const [status, setStatus] = useState<PaymentsStatusType>(
    PaymentsStatusType.Idle,
  );
  const [errorMessage, setErrorMessage] = useState("");
  // Snapshot of what the in-flight payment covers. The receipt can't derive
  // this from includedInPayment: refresh() after success flips the paid
  // invoices out of the payable set, which would empty the receipt.
  const [paidInvoices, setPaidInvoices] = useState<Invoice[]>([]);

  const shipmentMap = new Map(shipments.map((s) => [s.shipmentId, s]));

  // Shared with the screens that decide whether to offer the button at all,
  // so the button can't promise a dialog that opens on nothing.
  const invoicesToPay = paymentsPayableInvoices(invoices, shipments);

  // The invoices actually blocking booking start selected, because paying
  // those is what restores it. Keyed off blockingBooking rather than the due
  // date so a disputed invoice is never preselected — we've agreed not to
  // charge it, and paying it wouldn't unblock anything anyway.
  const [initialSelectedInvoiceIds] = useState(
    () =>
      new Set(
        invoicesToPay
          .filter((invoice) => invoice.blockingBooking)
          .map((invoice) => invoice.invoiceId),
      ),
  );
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState(
    initialSelectedInvoiceIds,
  );

  const includedInPayment = invoicesToPay.filter((invoice) =>
    selectedInvoiceIds.has(invoice.invoiceId),
  );

  useEffect(() => {
    if (open) {
      loadPaymentMethods();
    }
  }, [open]);

  async function loadPaymentMethods() {
    setLoading(true);
    setPaymentMethodsLoadFailed(false);
    try {
      const response = await paymentMethodsApi.getAllPaymentMethods();

      dispatch({
        type: "SET_METHODS",
        payload: {
          bankAccounts: response.bankAccounts,
          creditCards: response.creditCards,
          isChequePermitted: response.isChequePaymentPermitted,
          isEFTPermitted: response.isEftPaymentPermitted,
          isETransferPermitted: response.isEtransferPaymentPermitted,
          isWirePermitted: response.isWirePaymentPermitted,
          defaultMethod: response.defaultPaymentMethod,
        },
      });

      // Don't preselect the default payment method when it's an expired
      // credit card — the user must explicitly pick a usable one (ENG-4368)
      const defaultIsExpiredCard = response.creditCards.some(
        (card) =>
          card.stripePaymentMethodId === response.defaultPaymentMethod &&
          isCreditCardExpired(card.expiry),
      );
      setSelectedPaymentMethod(
        defaultIsExpiredCard ? undefined : response.defaultPaymentMethod,
      );
    } catch (e) {
      console.error("Error loading payment methods", e);
      setPaymentMethodsLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }

  function paymentMethod(): PaymentMethod {
    if (isBankAccount(paymentMethodsState, selectedPaymentMethod)) {
      return PaymentMethod.BankAccount;
    }
    if (isCreditCard(paymentMethodsState, selectedPaymentMethod)) {
      return PaymentMethod.CreditCard;
    }
    switch (selectedPaymentMethod) {
      case "cheque":
        return PaymentMethod.Cheque;
      case "etransfer":
        return PaymentMethod.InteracETransfer;
      case "eft":
        return PaymentMethod.Eft;
      case "wire":
        return PaymentMethod.Wire;
      case undefined:
      default:
        throw new Error("Unknown payment method");
    }
  }

  async function confirmPayment() {
    const invoicesBeingPaid = includedInPayment;
    setPaidInvoices(invoicesBeingPaid);
    setStatus(PaymentsStatusType.Processing);
    setErrorMessage("");

    const selectedMethodType = paymentsResolvePaymentMethodText(
      selectedPaymentMethod,
      paymentMethodsState,
    );

    try {
      const response = await paymentMethodsApi.payInvoices({
        payInvoicesRequest: {
          paymentMethod: paymentMethod(),
          ...(selectedPaymentMethod !== undefined
            ? { paymentMethodId: selectedPaymentMethod }
            : {}),
          paymentReference,
          invoiceIds: invoicesBeingPaid.map((invoice) => invoice.invoiceId),
        },
      });

      if (response.success) {
        // A card is charged on the spot; everything else settles later.
        setStatus(
          selectedMethodType === PaymentMethod.CreditCard
            ? PaymentsStatusType.Completed
            : PaymentsStatusType.Pending,
        );
      } else {
        setStatus(PaymentsStatusType.Failed);
        const specificErrorMessage =
          response.errorMessage || "Payment failed. Please try again.";
        setErrorMessage(specificErrorMessage);
        console.error(
          "Payment failed with specific message:",
          specificErrorMessage,
        );
      }

      // ENG-7021: a partial charge comes back as success = false, so refetching only on
      // success left the invoices that did go through still listed as owing.
      refresh();
    } catch (e: unknown) {
      setStatus(PaymentsStatusType.Failed);
      console.error("Network/API error:", e);
      setErrorMessage(
        "Unable to process payment. Please check your connection and try again.",
      );
    }
  }

  const paidTotalAmount = Number(
    paidInvoices.reduce((sum, invoice) => sum + invoice.amount, 0).toFixed(2),
  );

  const totalAmountToPay = Number(
    includedInPayment
      .reduce((sum, invoice) => sum + invoice.amount, 0)
      .toFixed(2),
  );

  const invoiceSummary = includedInPayment.reduce<InvoiceSummary>(
    (acc, invoice) => {
      if (invoice.invoiceType === InvoiceType.ShipmentCharge) {
        acc.shipment.count += 1;
        acc.shipment.total += invoice.amount;
      } else if (invoice.invoiceType === InvoiceType.AdditionalCharge) {
        acc.additional.count += 1;
        acc.additional.total += invoice.amount;
      }
      return acc;
    },
    {
      shipment: { count: 0, total: 0 },
      additional: { count: 0, total: 0 },
    },
  );

  return {
    loading,
    paymentMethodsLoadFailed,
    paymentMethodsState,
    reloadPaymentMethods: loadPaymentMethods,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    paymentReference,
    setPaymentReference,
    status,
    errorMessage,
    paidInvoices,
    paidTotalAmount,
    invoicesToPay,
    shipmentMap,
    includedInPayment,
    initialSelectedInvoiceIds,
    setSelectedInvoiceIds,
    totalAmountToPay,
    invoiceSummary,
    confirmPayment,
  };
}
